/* Importer un fichier, et voir ce qu'il va faire avant qu'il le fasse.
 *
 * L'écran tient en trois temps : on choisit ce qu'on importe, on dépose le
 * fichier, on lit le rapport. Rien n'est écrit tant que le bouton n'a pas été
 * pressé — et le rapport dit exactement ce qui sera écrit, et ce qui sera
 * laissé de côté, avec le numéro de ligne du tableur.
 */

import { useMemo, useRef, useState } from "react";
import { autorise, useNiveau } from "@/lib/session";
import {
  useAgents, useClients, useProduits, useUsines,
} from "@/lib/donnees";
import { dictionnaire, doublons, lire, sansLignes } from "@/domain/import";
import type { Dictionnaires, ErreurImport, LigneImport, Rapport } from "@/domain/import";
import {
  MODELES, NIVEAU_REQUIS, ORDRE_IMPORT, PREALABLES,
} from "@/domain/modeles-import";
import type { CleImport } from "@/domain/modeles-import";
import { FichierIllisible, lireClasseur, modeleCsv } from "@/lib/classeur";
import { contactsMalRattaches, ecrire } from "./actions";
import { Banner, Empty, Section, Tag } from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { useToast } from "@/ui/Toast";
import { entier } from "@/lib/format";

type Phase = "attente" | "lecture" | "pret" | "ecriture";

export function Import() {
  const niveau = useNiveau();
  const toast = useToast();
  const { donnees: usines } = useUsines();
  const { donnees: produits } = useProduits();
  const { donnees: clients } = useClients();
  const { donnees: agents, recharger: rechargerAgents } = useAgents();

  const permis = ORDRE_IMPORT.filter((c) => autorise(niveau, NIVEAU_REQUIS[c]));
  const [cle, setCle] = useState<CleImport>(permis[0] ?? "usines");
  const [phase, setPhase] = useState<Phase>("attente");
  const [nomFichier, setNomFichier] = useState<string | null>(null);
  const [rapport, setRapport] = useState<Rapport | null>(null);
  const [refusees, setRefusees] = useState<ErreurImport[]>([]);
  const [panne, setPanne] = useState<string | null>(null);
  const [survol, setSurvol] = useState(false);
  const champFichier = useRef<HTMLInputElement>(null);

  const modele = MODELES[cle];

  const dicos: Dictionnaires = useMemo(
    () => ({
      usines: dictionnaire(usines, (u) => u.id, (u) => u.code, (u) => u.nom),
      clients: dictionnaire(clients, (c) => c.id, (c) => c.nom),
      produits: dictionnaire(produits, (p) => p.id, (p) => p.ref),
      agents: dictionnaire(agents, (a) => a.id, (a) => a.email, (a) => a.nom),
    }),
    [usines, clients, produits, agents],
  );

  function remettre() {
    setRapport(null);
    setRefusees([]);
    setNomFichier(null);
    setPanne(null);
    setPhase("attente");
    if (champFichier.current) champFichier.current.value = "";
  }

  function changerType(c: CleImport) {
    setCle(c);
    remettre();
  }

  async function deposer(fichier: File) {
    setPhase("lecture");
    setPanne(null);
    setNomFichier(fichier.name);
    try {
      const rangees = await lireClasseur(fichier);
      const r = lire(rangees, modele, dicos);

      /* Deux contrôles que le modèle ne peut pas porter seul : les lignes qui
         se répètent dans le fichier, et — pour les contacts — celles qui ne
         savent pas à qui se rattacher. */
      const refus: ErreurImport[] = [...doublons(r.lignes, clesDe(cle))];
      if (cle === "contacts") {
        for (const l of contactsMalRattaches(r.lignes)) {
          refus.push({ ligne: l, message: "renseignez une usine ou un client, pas les deux" });
        }
      }

      setRapport(r);
      setRefusees(refus);
      setPhase("pret");
    } catch (e) {
      setPanne(
        e instanceof FichierIllisible ? e.message : "Fichier illisible.",
      );
      setPhase("attente");
    }
  }

  const retenues: LigneImport[] = rapport ? sansLignes(rapport.lignes, refusees) : [];
  const bloque = Boolean(rapport?.manquantes.length);

  async function importer() {
    if (!retenues.length) return;
    setPhase("ecriture");
    try {
      const r = await ecrire(cle, retenues);
      const dit =
        r.inchangees === undefined
          ? `${entier(r.creees)} créé${r.creees > 1 ? "s" : ""}, ${entier(r.majs)} mis à jour`
          : `${entier(r.creees)} prix posé${r.creees > 1 ? "s" : ""}, ` +
            `${entier(r.inchangees)} inchangé${r.inchangees > 1 ? "s" : ""}`;
      toast(`${modele.titre} : ${dit}.`);
      if (cle === "agents") rechargerAgents();
      remettre();
    } catch (e) {
      setPanne(e instanceof Error ? e.message : "L'écriture a échoué.");
      setPhase("pret");
    }
  }

  function telechargerModele() {
    const csv = modeleCsv(
      modele.champs.map((c) => c.titre),
      modele.champs.map((c) => c.aide ?? (c.requis ? "obligatoire" : "")),
    );
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `omnix-${modele.cle}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!permis.length) {
    return (
      <div className="pad">
        <Empty titre="Import réservé" detail="Seule la direction importe des fichiers." />
      </div>
    );
  }

  return (
    <div className="pad">
      <div className="imp-types">
        {permis.map((c) => (
          <button
            key={c}
            type="button"
            className={`imp-type${c === cle ? " on" : ""}`}
            onClick={() => changerType(c)}
          >
            <span className="imp-type-t">{MODELES[c].titre}</span>
            {PREALABLES[c] ? (
              <span className="imp-type-n">après {PREALABLES[c]}</span>
            ) : (
              <span className="imp-type-n">sans prérequis</span>
            )}
          </button>
        ))}
      </div>

      <Section
        titre={modele.titre}
        note={modele.propos}
        droite={
          <button type="button" className="btn btn-ghost btn-sm" onClick={telechargerModele}>
            <Icon n="importer" cls="" />
            Modèle CSV
          </button>
        }
      >
        <div className="imp-colonnes">
          {modele.champs.map((c) => (
            <div className="imp-col" key={c.cle}>
              <div className="imp-col-t">
                {c.titre}
                {c.requis ? <span className="imp-req" title="obligatoire">*</span> : null}
              </div>
              {c.aide ? <div className="imp-col-n">{c.aide}</div> : null}
            </div>
          ))}
        </div>
      </Section>

      {panne ? <div className="banner ko">{panne}</div> : null}

      {!rapport ? (
        <div
          className={`depot${survol ? " on" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setSurvol(true);
          }}
          onDragLeave={() => setSurvol(false)}
          onDrop={(e) => {
            e.preventDefault();
            setSurvol(false);
            const f = e.dataTransfer.files[0];
            if (f) void deposer(f);
          }}
        >
          <Icon n="importer" cls="depot-i" />
          <div className="depot-t">
            {phase === "lecture" ? "Lecture du fichier…" : "Déposez le fichier ici"}
          </div>
          <div className="depot-n">.xlsx ou .csv — rien ne sera écrit avant votre accord</div>
          <button
            type="button"
            className="btn"
            onClick={() => champFichier.current?.click()}
            disabled={phase === "lecture"}
          >
            Choisir un fichier
          </button>
          <input
            ref={champFichier}
            type="file"
            accept=".xlsx,.csv,.txt,.tsv"
            className="depot-champ"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void deposer(f);
            }}
          />
        </div>
      ) : (
        <Section
          titre={nomFichier ?? "Fichier"}
          note={`${entier(rapport.lues)} ligne${rapport.lues > 1 ? "s" : ""} lue${rapport.lues > 1 ? "s" : ""}`}
          droite={
            <button type="button" className="btn btn-ghost btn-sm" onClick={remettre}>
              Changer de fichier
            </button>
          }
        >
          <div className="imp-bilan">
            <Tag ton={retenues.length ? "ok" : "warn"} dot>
              {entier(retenues.length)} à importer
            </Tag>
            {rapport.erreurs.length ? (
              <Tag ton="crit" dot>{entier(rapport.erreurs.length)} en erreur</Tag>
            ) : null}
            {refusees.length ? (
              <Tag ton="warn" dot>{entier(refusees.length)} écartée{refusees.length > 1 ? "s" : ""}</Tag>
            ) : null}
          </div>

          {bloque ? (
            <Banner>
              Colonne{rapport.manquantes.length > 1 ? "s" : ""} obligatoire
              {rapport.manquantes.length > 1 ? "s" : ""} absente
              {rapport.manquantes.length > 1 ? "s" : ""} du fichier :{" "}
              <strong>{rapport.manquantes.join(", ")}</strong>. Rien ne peut être importé tant
              qu'elle{rapport.manquantes.length > 1 ? "s" : ""} manque
              {rapport.manquantes.length > 1 ? "nt" : ""}.
            </Banner>
          ) : null}

          {rapport.inconnues.length ? (
            <Banner>
              Colonne{rapport.inconnues.length > 1 ? "s" : ""} ignorée
              {rapport.inconnues.length > 1 ? "s" : ""} : {rapport.inconnues.join(", ")}.
            </Banner>
          ) : null}

          <ListeErreurs titre="Lignes en erreur" erreurs={rapport.erreurs} />
          <ListeErreurs titre="Lignes écartées" erreurs={refusees} />

          {retenues.length ? (
            <Apercu lignes={retenues} modele={modele} />
          ) : (
            <Empty
              titre="Rien à importer"
              detail="Corrigez le fichier et redéposez-le : les numéros de ligne sont ceux du tableur."
            />
          )}

          <div className="imp-actions">
            <button type="button" className="btn btn-ghost" onClick={remettre}>
              Annuler
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!retenues.length || bloque || phase === "ecriture"}
              onClick={() => void importer()}
            >
              {phase === "ecriture"
                ? "Écriture…"
                : `Importer ${entier(retenues.length)} ligne${retenues.length > 1 ? "s" : ""}`}
            </button>
          </div>
        </Section>
      )}
    </div>
  );
}

/** Clé naturelle d'un type : ce qui ne doit pas se répéter dans le fichier. */
function clesDe(cle: CleImport): string[] {
  switch (cle) {
    case "usines": return ["code"];
    case "produits": return ["usine_id", "ref"];
    case "prix_usine": return ["produit_id"];
    case "prix_agent": return ["agent_id", "produit_id"];
    case "agents": return ["email"];
    case "contacts": return ["usine_id", "client_id", "nom"];
  }
}

const ERREURS_MONTREES = 30;

function ListeErreurs({ titre, erreurs }: { titre: string; erreurs: readonly ErreurImport[] }) {
  if (!erreurs.length) return null;
  const montrees = erreurs.slice(0, ERREURS_MONTREES);
  return (
    <div className="imp-erreurs">
      <div className="imp-erreurs-t">{titre}</div>
      <ul>
        {montrees.map((e, i) => (
          <li key={`${e.ligne}-${e.colonne ?? ""}-${i}`}>
            <span className="imp-ligne">l.{e.ligne}</span>
            {e.colonne ? <span className="imp-colonne">{e.colonne}</span> : null}
            <span>{e.message}</span>
          </li>
        ))}
      </ul>
      {erreurs.length > montrees.length ? (
        <div className="imp-erreurs-n">
          et {entier(erreurs.length - montrees.length)} autre
          {erreurs.length - montrees.length > 1 ? "s" : ""}
        </div>
      ) : null}
    </div>
  );
}

const APERCU = 8;

function Apercu({
  lignes, modele,
}: { lignes: readonly LigneImport[]; modele: (typeof MODELES)[CleImport] }) {
  /* On ne montre pas les identifiants résolus : personne ne reconnaît une
     usine à son uuid. Les colonnes de référence s'affichent telles qu'elles
     ont été lues, c'est-à-dire déjà validées. */
  const colonnes = modele.champs.slice(0, 6);
  return (
    <div className="tablewrap imp-apercu">
      <table className="tbl">
        <thead>
          <tr>
            <th className="t-court">Ligne</th>
            {colonnes.map((c) => <th key={c.cle}>{c.titre}</th>)}
          </tr>
        </thead>
        <tbody>
          {lignes.slice(0, APERCU).map((l) => (
            <tr key={l.ligne}>
              <td className="mono">{l.ligne}</td>
              {colonnes.map((c) => (
                <td key={c.cle}>{affiche(l.valeurs[c.cle], Boolean(c.reference))}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {lignes.length > APERCU ? (
        <div className="imp-erreurs-n">
          et {entier(lignes.length - APERCU)} ligne
          {lignes.length - APERCU > 1 ? "s" : ""} de plus
        </div>
      ) : null}
    </div>
  );
}

function affiche(v: unknown, reference: boolean): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "oui" : "non";
  if (reference) return "✓";
  return String(v);
}
